import { CamelCasedProperties, CamelCasedPropertiesDeep } from 'type-fest';
import { z } from 'zod/v4';
import { createDefinitionSchema, unitOptionsSchema } from '../../utils';

/**
 * Position options for the legend
 */
export type HeatmapLegendPosition = z.infer<typeof heatmapLegendPositionSchema>;
export const heatmapLegendPositionSchema = z.enum(['Bottom', 'Right']);

/**
 * Options for the heatmap legend
 */
export interface HeatmapLegendOptions extends z.TypeOf<typeof heatmapLegendOptionsSchema> {}

const heatmapLegendOptionsSchema = z.object({
  show: z.boolean().describe('if true, show the legend'),
  position: heatmapLegendPositionSchema.describe('placement of the legend'),
});

/**
 * Color scale options for the heatmap
 */
export type HeatmapColorScale = z.infer<typeof heatMapColorScalesSchema>;
export const heatMapColorScalesSchema = z.enum([
  'turbo',
  'viridis',
  'inferno',
  'greenBlue',
  'orangeRed',
  'greys',
  'blues',
  'greens',
  'oranges',
  'reds',
  'cividis',
]);

export interface HeatMapYAxisOptions extends z.TypeOf<typeof heatMapYAxisOptionsSchema> {}
export const heatMapYAxisOptionsSchema = z.object({
  show: z.boolean().optional(),
  label: z.string().optional(),
  unit: unitOptionsSchema.optional(),
});

/**
 * Options for heatmap color rendering
 */
export interface HeatmapColorOptions extends z.TypeOf<typeof heatmapColorOptionsSchema> {}

const heatmapColorOptionsSchema = z.object({
  /**
   * value associated with the lower end of the color scale (defaults to 0)
   */
  min: z
    .number()
    .default(0)
    .optional()
    .describe('value associated with the lower end of the color scale (defaults to 0)'),
  /**
   * value associated with the upper end of the color scale (defaults to max value in the data set)
   */
  max: z
    .number()
    .optional()
    .describe('value associated with the upper end of the color scale (defaults to max value in the data set)'),
  /**
   * which color scale to use
   */
  scale: heatMapColorScalesSchema.describe('which color scale to use').optional().default('orangeRed'),
  /**
   * invertScale reverses the color scale
   */
  invertScale: z.boolean().describe('reverses the color scale').optional().default(false),
  /**
   * if true, leave 0-value / missing-value / negative-value cells empty
   */
  hideZeros: z
    .boolean()
    .describe('if true, leave 0-value / missing-value / negative-value cells empty')
    .optional()
    .default(true),
});

/**
 * Type for the spec-driven `DashboardPanel` variant of the HeatMap.
 */
export interface HeatMapSpec extends z.TypeOf<typeof heatMapSpecSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the HeatMap.
 */
export const heatMapSpecSchema = z.object({
  legend: heatmapLegendOptionsSchema.describe('legend-related options').optional(),
  color: heatmapColorOptionsSchema.describe('color-related options').optional(),
  y_axis: heatMapYAxisOptionsSchema.optional(),
});

/**
 * The schema for a HeatMap `DashboardPanel` definition.
 */
export interface HeatMapDefinition extends z.TypeOf<typeof heatmapDefinitionSchema> {}
export const heatmapDefinitionSchema = createDefinitionSchema('HeatMap', heatMapSpecSchema);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the HeatMap.
 *
 * Currently, it is the same as the spec-driven variant, but it may need to change in the future to
 * do things like flatten a specific property or some other transform (examples can be found in pie chart).
 */
export const heatMapVizSchema = heatMapSpecSchema;

type HeatMapVizSchemaType = z.TypeOf<typeof heatMapVizSchema>;

/**
 * Base type for the props-driven `Viz` variant of the HeatMap generated from the Zod schema and
 * converting all snake_case properties to camelCase (exception for `y_axis`, which is only shallowly camelCased).
 * This interface should be extended in `viz-framework` to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface HeatMapPropsBase
  extends CamelCasedPropertiesDeep<Omit<HeatMapVizSchemaType, 'y_axis'>>,
    CamelCasedProperties<Pick<HeatMapVizSchemaType, 'y_axis'>> {}
